/**
 * SOLID Review
 * S — Strong. One concern: name the browser's concretes the frontend graph is assembled from.
 * O — Good. A new host resource is one field.
 * L — N/A.
 * I — Strong. One function.
 * D — Good. The composition root: it may know the concretes — the Apollo client, the REST client, the
 *     stores, the websocket monitor, the CMP — and it is the only place that does.
 */
import { apolloClient, onWsConnectionStateChange, reidentify } from '../../infrastructure/graphql/apolloClient';
import { RestApiClient } from '../../infrastructure/contract/clients';
import type { IPortGraphQLDomainClient } from '../../infrastructure/contract/ports';
import { API_BASE } from '../../infrastructure/api/apiBase';
import { useGameStore } from '../../infrastructure/state/gameStore';
import { useAuthStore } from '../../infrastructure/state/authStore';
import { createConsentGateway } from '../../infrastructure/consent/ConsentGateway';
import { readEnv } from '../../shared/viteEnv';
import { resolveHealthUrl } from '../../shared/appConfig';
import { PortObserverConsole } from './PortObserverConsole';
import type { IConfigDepFrontendPlatform } from './FrontendAssembly';

/**
 * THE BROWSER, AS THE FRONTEND GRAPH SEES IT (frontend strict-ports plan, FE1d, 2026-10-03) — the twin
 * of the backend's `HostPlatform`. One Apollo client for the six GraphQL wire sockets, one REST client
 * for the two REST ones, the stores, the websocket's state and the consent platform (FE4b, from App.tsx)
 * for Infrastructure, the console for the observer and for a half-wired graph.
 */
export function browserPlatform(): IConfigDepFrontendPlatform {
  const platform: IConfigDepFrontendPlatform = {
    clients: {
      // `apolloClient` is `ApolloClient<…>`; the socket's type is the `Pick<>` of it adapters use (FE0).
      graphql: apolloClient as unknown as IPortGraphQLDomainClient,
      rest: new RestApiClient(API_BASE),
    },
    host: {
      tableStore: useGameStore,
      sessionStore: useAuthStore,
      connection: { onStateChange: onWsConnectionStateChange, reidentify },
      // Build-time config: unset (dev, or a deployment without a Funding Choices account), the gateway
      // falls back to the opt-out default after a short timeout, so the app stays usable. Read through
      // `readEnv`: the port and UML generators assemble this graph under Node, without Vite.
      consentGateway: createConsentGateway(readEnv('VITE_GOOGLE_FC_PUBLISHER_ID') ?? null),
      healthUrl: resolveHealthUrl(
        /* c8 ignore next -- the `{}` arm needs a loader with no import.meta.env; unreachable under Vite. */
        (import.meta as unknown as { env?: Record<string, unknown> }).env ?? {},
        window.location,
      ),
    },
    observer: new PortObserverConsole(),
    report: (message) => console.error('[FrontendAssembly]', message),
  };
  return platform;
}
